import Icon from '@/components/snippets/icon/icon'
import clsx from 'clsx'
import { useEffect, useRef, useState } from 'preact/hooks'
import { globalState } from '@/components/helpers/utils'
import LoaderSpin from '@/components/libs/searchspring/components/LoaderSpin'
import { VisibilityElement } from '@/components/custom-elements/visibility'

// Utility function moved outside components
const getStockStatus = (variant, stockLevels) => {
  // If variant doesn't exist, return out-of-stock status
  if (!variant) return { inStock: false, text: '' }

  // Actual inventory quantity
  const quantity = variant.inventory_quantity

  // Check if there's a specific message for quantity 0
  const zeroStockLevel = stockLevels.find(stock => stock.max_quantity === 0)

  // If quantity is 0 and we have a specific message for it, use that message
  if (quantity <= 0 && zeroStockLevel) {
    return {
      inStock: false,
      text: zeroStockLevel.message,
      warning: true,
      stockLevel: quantity,
      textColour: zeroStockLevel.message_colour
    }
  }

  // If quantity is 0 but no specific message, use default out of stock message
  if (quantity <= 0) {
    return { inStock: false, text: 'Out of stock', warning: true, stockLevel: quantity, textColour: '#b3b3b3' }
  }

  // Check against thresholds (sorted from highest to lowest)
  for (const stock of stockLevels) {
    if (quantity <= stock.max_quantity) {
      return {
        inStock: true,
        text: stock.message,
        warning: true,
        stockLevel: quantity,
        textColour: stock.message_colour
      }
    }
  }

  // If no threshold is matched, item is fully in stock
  return {
    inStock: true,
    text: '',
    warning: false,
    stockLevel: quantity,
    text_color: ''
  }
}

export function ResultQuickAdd({ result }) {
  const [selectedSize, setSelectedSize] = useState('')
  const [loading, setLoading] = useState(false)
  const modalRef = useRef(null)
  const randomId = Math.random().toString(36).substring(7)
  const modalId = `Product-Quick-Add-${result.id}-${randomId}`
  const [openSizeSelector, setOpenSizeSelector] = useState(false)

  const handleClick = () => {
    if (!selectedSize) {
      setOpenSizeSelector(!openSizeSelector)
    } else {
      handleAddToCart()
    }
  }

  const handleAddToCart = async () => {
    if (!selectedSize) return

    const variant = result.variants.find(v => v.option2 === selectedSize)
    if (!variant) return

    if (window.Cart) {
      setLoading(true)
      try {
        await window.Cart.addLineItems({
          id: variant.id,
          quantity: 1
        })
        setSelectedSize('')
        modalRef.current?.handleHide()
      } catch (error) {
        console.error('Error adding item to cart:', error)
      } finally {
        setLoading(false)
      }
    }
  }

  return (
    <x-modal tabIndex={-1} ref={modalRef} data-id={modalId} className={'flex data-visible:md:opacity-100 md:opacity-0 group-hover/card:md:opacity-100 transition-opacity justify-end items-end group/size-selector'} data-hide-on-blur={true}>
      <button type="button" aria-label="Open Quick Add" className="p-2 overflow-hidden rounded-full bg-brand-green text-brand-black" data-modal-role="open" data-modal-parent={modalId}>
        <Icon id={'bag'} className={'icon-md'} />
      </button>
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden !h-fit transition-all duration-300 max-h-0 bg-neutral-medium teeth group-data-visible/size-selector:max-h-[400px] group-data-visible/size-selector:animation-bounce-in">
        <div className="flex flex-col gap-2 p-4 pt-8 md:p-6">
        {/* Size Selector */}
        <div className="relative transition-opacity duration-300 delay-500 opacity-0 group-data-visible/size-selector:opacity-100">
          <SizeSelector openSizeSelector={openSizeSelector} variants={result.variants} setSelectedSize={setSelectedSize} selectedSize={selectedSize} resultId={result.id} />
        </div>

        {/* Add to Cart Button */}
        <button
          onClick={handleClick}
          type="button"
          aria-label="Add to Cart"
          className={clsx(
            'mb-4 text-sm btn btn-md md:btn-lg btn-accent-2 relative',
            loading && 'text-opacity-0'
          )}
        >
          <span>{!selectedSize ? 'SELECT SIZE' : 'ADD TO CART'}</span>
          {loading && <LoaderSpin /> }
        </button>

        <button type="button" aria-label="Close Quick Add" className="self-end p-2 overflow-hidden rounded-full w-fit bg-neutral-tertiary text-brand-black" data-modal-role="close" data-modal-parent={modalId}>
          <Icon id={'close'} className={'icon-md'} />
        </button>
        </div>
      </div>
    </x-modal>
  )
}

const SizeSelector = ({ variants, setSelectedSize, selectedSize, resultId, openSizeSelector }) => {
  const stockLevels = globalState?.metaobjects.stock_indicators?.sort((a, b) => b.max_quantity - a.max_quantity) || []
  const randomId = Math.random().toString(36).substring(7)
  const closeButtonRef = useRef(null)
  const modalRef = useRef(null)

  useEffect(() => {
    if (modalRef.current) {
      const id = modalRef.current.getAttribute('data-id')
      VisibilityElement.dispatchShow(id)
    }
  }, [openSizeSelector])

  // Helper function to get status for a specific size
  const getStatusForSize = (size) => {
    const variant = variants.find(v => v.option2 === size)
    return getStockStatus(variant, stockLevels)
  }

  // Generate a unique ID for this dropdown
  const sizeDropdownId = `Size-Dropdown-${resultId}-${randomId}`

  // Handle keyboard navigation
  const handleKeyDown = (e, variant) => {
    const status = getStatusForSize(variant.option2)

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (status.inStock) {
        setSelectedSize(variant.option2)
        // Close the dropdown after selection
        if (closeButtonRef.current) {
          closeButtonRef.current.click()
        }
      }
    }
  }

  return (
    <div className="relative w-full">
      <x-modal ref={modalRef} data-id={sizeDropdownId} className="w-full group/size-dropdown" data-hide-on-blur={true}>
        {/* Custom dropdown button */}
        <button
          aria-haspopup="listbox"
          aria-labelledby="size-selector-label"
          className={clsx('flex items-center justify-between w-full p-3 text-left',
            selectedSize ? 'bg-brand-yellow text-brand-black' : 'bg-white text-neutral-secondary')}
          data-modal-role="toggle"
          data-modal-parent={sizeDropdownId}
        >
          <span id="size-selector-label" className="paragraph-md grow">
            {selectedSize || 'Select size'}
          </span>
          <Icon
            id="chevron-down"
            className={clsx('icon-md text-neutral-black transform transition-transform',
              'group-data-visible/size-dropdown:rotate-180')}
            aria-hidden="true"
          />
        </button>

        {/* Dropdown options */}
        <div
          className={clsx(
            'relative z-10 w-full bg-white overflow-hidden transition-all duration-300 max-h-0 border-t border-neutral-medium',
            'group-data-visible/size-dropdown:max-h-96'
          )}
          role="listbox"
          aria-labelledby="size-selector-label"
        >
          {variants.map((variant) => {
            const status = getStatusForSize(variant.option2)
            return (
              <div
                key={variant.id}
                role="option"
                aria-selected={selectedSize === variant.option2}
                tabIndex={status.inStock ? 0 : -1}
                className={`
                  p-3 flex justify-between items-center cursor-pointer border-b border-neutral-medium
                  ${status.inStock ? 'hover:bg-gray-100' : 'opacity-50 !cursor-not-allowed'}
                  ${selectedSize === variant.option2 ? 'bg-brand-yellow' : ''}
                `}
                onClick={() => {
                  if (status.inStock) {
                    setSelectedSize(variant.option2)
                    // Close the dropdown after selection
                    if (closeButtonRef.current) {
                      closeButtonRef.current.click()
                    }
                  }
                }}
                onKeyDown={(e) => handleKeyDown(e, variant)}
                aria-disabled={!status.inStock}
              >
                <span>{variant.option2}</span>
                {status.text && (
                  <span
                    className="xs-max:hidden"
                    style={status.warning ? { color: status.textColour } : {}}
                    aria-live="polite"
                  >
                    {status.text}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      </x-modal>

      {/* Hidden native select for form submission */}
      <select
        value={selectedSize || ''}
        onChange={(e) => setSelectedSize(e.target.value)}
        className="sr-only"
        id={`size-select-${randomId}`}
        name="size"
      >
        <option value="">Select size</option>
        {variants.map((variant) => (
          <option
            key={variant.id}
            value={variant.option2}
            disabled={!getStatusForSize(variant.option2).inStock}
          >
            {variant.option2}
          </option>
        ))}
      </select>
    </div>
  )
}

export default SizeSelector
